/**
 * PDF Report Design System - Corporate Professional Theme
 * Centralized design tokens for consistent styling across all PDF pages
 */

export const colors = {
  // Brand colors
  primary: '#336858',
  primaryDark: '#1e4a3a',
  primaryLight: '#4a8a76',

  // Secondary accent
  accent: '#2c5282',

  // Text hierarchy
  text: {
    primary: '#1a202c',
    secondary: '#2d3748',
    muted: '#4a5568',
    light: '#718096',
    inverse: '#ffffff',
  },

  // Backgrounds
  background: {
    white: '#ffffff',
    header: '#336858',
    section: '#f7fafc',
    highlight: '#e6fffa',
    table: '#edf2f7',
    tableAlt: '#f7fafc',
  },

  // Borders
  border: {
    strong: '#336858',
    medium: '#cbd5e0',
    light: '#e2e8f0',
    subtle: '#edf2f7',
  },

  // Chart colors (corporate palette)
  chart: [
    '#336858', // primary green
    '#2c5282', // corporate blue
    '#744210', // brown
    '#553c9a', // purple
    '#b7791f', // gold
    '#2f855a', // dark green
    '#3182ce', // light blue
    '#805ad5', // violet
    '#dd6b20', // orange
    '#38a169', // green
  ],
};

export const typography = {
  // Page titles (e.g., "INFORME DE AUDIENCIA")
  pageTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.primaryDark,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },

  // Section titles (e.g., "REPORTE DE AUDIENCIA")
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
    borderBottomStyle: 'solid',
  },

  // Subsection titles
  subsection: {
    fontSize: 12,
    fontWeight: 'semibold',
    color: colors.text.primary,
    marginBottom: 8,
  },

  // Body text
  body: {
    fontSize: 11,
    fontWeight: 'normal',
    color: colors.text.secondary,
    lineHeight: 1.5,
  },

  // Small descriptive text
  caption: {
    fontSize: 9,
    fontWeight: 'normal',
    color: colors.text.muted,
    lineHeight: 1.4,
  },

  // Table header text
  tableHeader: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.text.inverse,
  },

  // Table cell text
  tableCell: {
    fontSize: 10,
    fontWeight: 'normal',
    color: colors.text.secondary,
  },

  // Footer text
  footer: {
    fontSize: 9,
    fontWeight: 'normal',
    color: colors.text.muted,
  },

  // Large metric numbers
  metric: {
    fontSize: 36,
    fontWeight: 'bold',
    color: colors.primary,
  },

  // Metric labels
  metricLabel: {
    fontSize: 10,
    fontWeight: 'semibold',
    color: colors.text.primary,
    textTransform: 'uppercase',
  },

  // Chart descriptions
  chartDescription: {
    fontSize: 9,
    fontWeight: 'normal',
    color: colors.text.muted,
    fontStyle: 'italic',
    marginTop: 8,
  },
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 20,
  xl: 32,
  xxl: 48,
};

export const layout = {
  // Page dimensions (Letter size)
  page: {
    width: 612,
    height: 792,
  },

  // Page margins
  margin: {
    top: 60,
    right: 40,
    bottom: 60,
    left: 40,
  },

  // Header/Footer heights
  headerHeight: 50,
  footerHeight: 40,

  // Content width
  contentWidth: 532, // page.width - margin.left - margin.right
};

// Pre-composed style objects for common patterns
export const commonStyles = {
  // Metric card container
  metricCard: {
    backgroundColor: colors.background.section,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    borderLeftStyle: 'solid',
    padding: spacing.md,
    marginBottom: spacing.md,
  },

  // Data table styles
  table: {
    container: {
      marginVertical: spacing.md,
    },
    headerRow: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
    },
    row: {
      flexDirection: 'row',
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border.light,
      borderBottomStyle: 'solid',
    },
    rowAlt: {
      backgroundColor: colors.background.table,
    },
  },

  // Chart container
  chartContainer: {
    marginVertical: spacing.md,
    padding: spacing.sm,
  },

  // Page header bar
  headerBar: {
    backgroundColor: colors.primary,
    height: layout.headerHeight,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
  },

  // Page footer
  footerBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: layout.footerHeight,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border.light,
    borderTopStyle: 'solid',
  },
};

// Helper function to get chart color by index
export const getChartColor = (index, opacity = 1) => {
  const color = colors.chart[index % colors.chart.length];
  if (opacity === 1) return color;

  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export default {
  colors,
  typography,
  spacing,
  layout,
  commonStyles,
  getChartColor,
};
